/**
 * Making foreign markup safe to keep, once, at the door.
 *
 * The composer preserves far more than it used to — unknown tags, whole
 * `<style>` blocks, every attribute a block does not otherwise model — and all
 * of that is markup somebody else wrote. It is checked here, on the way in,
 * rather than on the way out: the document is the source of truth, and
 * everything downstream is generated from it, so this is the only place the
 * check has to hold.
 *
 * The rule is a deny-list rather than an allow-list, which is the opposite of
 * the usual advice and deliberate. An allow-list is what a sanitiser wants when
 * it knows the vocabulary; ours is open by design — `<o:p>` from a Word export
 * and `<v:roundrect>` from an Outlook one are exactly the elements worth
 * keeping. So the elements that can *execute* or *fetch* are named, and the
 * rest is left alone.
 */

/** Elements that run code, pull remote resources, or navigate. */
export const DANGEROUS_ELEMENTS = [
  "script",
  "iframe",
  "frame",
  "frameset",
  "object",
  "embed",
  "applet",
  "form",
  "base",
  "meta",
  "link",
].join(",");

/** Attributes that carry a URL, and so carry a scheme worth checking. */
export const URL_ATTRIBUTES = [
  "href",
  "src",
  "srcset",
  "action",
  "formaction",
  "background",
  "poster",
  "xlink:href",
  "data",
];

/**
 * Schemes an email may point at. `cid:` is how an inline attachment is
 * referenced, and `data:image/` is how a small logo is embedded — everything
 * else that can execute is not here.
 */
export const SAFE_URL =
  /^(?:https?:|mailto:|tel:|cid:|data:image\/|[#/?]|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/** CSS that executes, binds behaviour, or fetches. */
export const DANGEROUS_CSS =
  /expression\s*\(|behavior\s*:|-moz-binding|javascript:|@import/i;

function sanitizeStyleAttribute(element: Element): void {
  const style = element.getAttribute("style");
  if (style && DANGEROUS_CSS.test(style)) {
    // One bad declaration does not make the rest of the design unsafe, but
    // splitting a value apart is how a bypass gets through. Drop the attribute.
    element.removeAttribute("style");
  }
}

function sanitizeAttributes(element: Element): void {
  for (const { name, value } of Array.from(element.attributes)) {
    const lower = name.toLowerCase();
    if (lower.startsWith("on")) {
      element.removeAttribute(name);
      continue;
    }
    if (URL_ATTRIBUTES.includes(lower) && !SAFE_URL.test(value.trim())) {
      element.removeAttribute(name);
    }
  }
  sanitizeStyleAttribute(element);
}

/**
 * Strip what can execute, and leave the design alone.
 *
 * Mutates `root` in place. A `<style>` block keeps its rules unless they are
 * the kind that runs — `expression()` is Internet Explorer's way of putting
 * script in a stylesheet, and `@import` fetches a stylesheet the recipient
 * never asked for.
 */
export function sanitizeForEmail(root: ParentNode): void {
  for (const element of Array.from(root.querySelectorAll("style"))) {
    if (DANGEROUS_CSS.test(element.textContent ?? "")) element.remove();
  }
  for (const element of Array.from(root.querySelectorAll(DANGEROUS_ELEMENTS))) {
    element.remove();
  }
  for (const element of Array.from(root.querySelectorAll("*"))) {
    sanitizeAttributes(element);
  }
}
